import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { ApiRequest } from "GlobalFunctions";

interface GuideDetailDialogProps {
  open: boolean;
  onClose: () => void;
  client_guide_id: number | null;
}

const SectionTitle = ({ label }: { label: string }) => (
  <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9e9e9e", display: "block", mb: 1.5 }}>
    {label}
  </Typography>
);

const Section = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ background: "#f9f9f9", borderRadius: 2, p: 2 }}>
    {children}
  </Box>
);

export default function GuideDetailDialog({ open, onClose, client_guide_id }: GuideDetailDialogProps) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (open && client_guide_id) {
      setData(null);
      setLoading(true);
      ApiRequest({
        url: "/guides/clientGuideDetail",
        method: "GET",
        query: { client_guide_id },
        headers: { ltkn: localStorage.getItem("ltkn") },
        setResponse: (response: any) => {
          if (response.status === "success") {
            setData(response.data);
          }
          setLoading(false);
        },
      });
    }
  }, [open, client_guide_id]);

  const statusConfig = (status_id: number) => {
    if (status_id === 2) return { label: "Entregado", color: "success" as const };
    if (status_id === 3) return { label: "Parcial", color: "warning" as const };
    if (status_id === 4) return { label: "Devuelto", color: "error" as const };
    return { label: "Pendiente", color: "default" as const };
  };

  const hasNoDetails = data &&
    !data.message &&
    !data.undelivered_concept &&
    data.payments?.length === 0 &&
    data.images?.length === 0 &&
    data.prestamos?.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Detalle de guía</Typography>
          {data && (
            <Chip
              label={statusConfig(data.status_id).label}
              color={statusConfig(data.status_id).color}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        {loading && (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && data && (
          <Box display="flex" flexDirection="column" gap={1.5}>

            {/* Info general */}
            {(data.user_name?.trim() || data.undelivered_concept || data.message) && (
              <Section>
                <SectionTitle label="General" />
                <Box display="flex" flexDirection="column" gap={1}>
                  {data.user_name?.trim() && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Realizado por</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.user_name.trim()}</Typography>
                    </Box>
                  )}
                  {data.undelivered_concept && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Concepto devolución</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.undelivered_concept}</Typography>
                    </Box>
                  )}
                  {data.message && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Comentario</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.message}</Typography>
                    </Box>
                  )}
                </Box>
              </Section>
            )}

            {/* Pagos */}
            {data.payments?.length > 0 && (
              <Section>
                <SectionTitle label="Pagos recibidos" />
                <Box display="flex" flexDirection="column" gap={0.5}>
                  {data.payments.map((p: any, i: number) => (
                    <Box key={i} display="flex" justifyContent="space-between" alignItems="center"
                      sx={{ py: 0.75, borderBottom: "1px solid #efefef" }}>
                      <Typography variant="body2" color="text.secondary">{p.payment_method}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        ${parseFloat(p.payment_amount || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ pt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Total</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                      ${data.total_paid?.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Section>
            )}

            {/* Préstamos */}
            {data.prestamos?.length > 0 && (
              <Section>
                <SectionTitle label="Préstamos" />
                <Box display="flex" flexDirection="column" gap={0.5}>
                  {data.prestamos.map((p: any, i: number) => (
                    <Box key={i} display="flex" justifyContent="space-between" alignItems="center"
                      sx={{ py: 0.75, borderBottom: "1px solid #efefef" }}>
                      <Typography variant="body2" color="text.secondary">{p.producto}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.cantidad}</Typography>
                    </Box>
                  ))}
                </Box>
              </Section>
            )}

            {/* Fotos */}
            {data.images?.length > 0 && (
              <Section>
                <SectionTitle label="Fotos" />
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {data.images.map((url: string, i: number) => (
                    <a href={url} target="_blank" rel="noreferrer" key={i}>
                      <Box
                        component="img"
                        src={url}
                        alt={`foto-${i + 1}`}
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                          borderRadius: 1.5,
                          transition: "opacity .2s",
                          "&:hover": { opacity: 0.85 },
                        }}
                      />
                    </a>
                  ))}
                </Box>
              </Section>
            )}

            {hasNoDetails && (
              <Box py={3} textAlign="center">
                <Typography variant="body2" color="text.secondary">Sin detalles registrados.</Typography>
              </Box>
            )}

          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} variant="outlined" size="small" sx={{ borderRadius: 2, textTransform: "none", px: 3 }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
